/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Play, Pause, RefreshCw, CheckCircle2, AlertCircle, Clock, MessageCircle, Mail, Activity, Send, History, FlaskConical, Plus, Trash2, BarChart3 } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

function fmtRelative(iso) {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) {
    const s = Math.round(-ms / 1000);
    if (s < 60) return `in ${s}s`;
    if (s < 3600) return `in ${Math.round(s / 60)}m`;
    return `in ${Math.round(s / 3600)}h`;
  }
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

function fmtInterval(sec) {
  if (!sec) return "—";
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h`;
  return `${Math.round(sec / 86400)}d`;
}

function StatusBadge({ status }) {
  if (status === "ok") return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-medium">
      <CheckCircle2 className="w-3 h-3" /> ok
    </span>
  );
  if (status === "error") return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 text-xs font-medium">
      <AlertCircle className="w-3 h-3" /> error
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 text-[var(--text-mute)] text-xs">
      <Clock className="w-3 h-3" /> pending
    </span>
  );
}

function JobIcon({ id }) {
  const Icon = id?.startsWith("wa_") ? MessageCircle : Mail;
  const color = id?.startsWith("wa_") ? "text-emerald-300" : "text-sky-300";
  return <Icon className={`w-4 h-4 ${color}`} />;
}

export default function BackgroundJobsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState({});
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get("/admin/wa-cron-status");
      setData(res);
    } catch (e) {
      toast.error("Failed to load scheduler status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 10 s
  useEffect(() => {
    const t = setInterval(() => { setTick((n) => n + 1); load(); }, 10000);
    return () => clearInterval(t);
  }, [load]);

  const triggerJob = async (jobId) => {
    setTriggering((t) => ({ ...t, [jobId]: true }));
    try {
      const { data: res } = await api.post(`/admin/scheduler/trigger/${jobId}`);
      if (res.ok) {
        toast.success(`Triggered ${jobId} — refreshing in 3 s…`);
        setTimeout(load, 3000);
      } else {
        toast.error(res.error || "Failed to trigger");
      }
    } catch (e) {
      toast.error("Trigger request failed");
    } finally {
      setTriggering((t) => ({ ...t, [jobId]: false }));
    }
  };

  const togglePause = async (job) => {
    const action = job.paused ? "resume" : "pause";
    const label = job.name || job.id;
    if (!job.paused && !window.confirm(`Pause "${label}"?\n\nStudents ko iss job ke reminders/notifications NAHI jayenge jab tak aap resume nahi karte. Paused state production redeploy ke baad bhi same rahegi.`)) return;
    try {
      const { data: res } = await api.post(`/admin/scheduler/${action}/${job.id}`);
      if (res.ok) {
        toast.success(`${action === "pause" ? "Paused" : "Resumed"}: ${label}`);
        load();
      } else {
        toast.error(res.error || `Failed to ${action}`);
      }
    } catch {
      toast.error(`${action} request failed`);
    }
  };

  if (loading) return <div className="text-[var(--text-mute)] text-sm py-8 text-center">Loading scheduler…</div>;
  if (!data) return <div className="text-red-300 text-sm py-8 text-center">Could not load scheduler status.</div>;

  const jobs = data.jobs || [];
  const nx = data.nextel_configured || {};
  const counts = data.counts || {};

  return (
    <div className="space-y-5" data-testid="bg-jobs-panel">
      {/* Header strip — Nextel + counts summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="glass rounded-xl p-3" data-testid="bg-jobs-card-nextel">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-mute)] mb-1">Nextel send_template</div>
          <div className={`text-sm font-semibold ${nx.send_template ? "text-emerald-300" : "text-red-300"}`}>
            {nx.send_template ? "✓ configured" : "✗ not configured"}
          </div>
        </div>
        <div className="glass rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-mute)] mb-1">Nextel CRM audience</div>
          <div className={`text-sm font-semibold ${nx.audience_webhook ? "text-emerald-300" : "text-red-300"}`}>
            {nx.audience_webhook ? "✓ configured" : "✗ not configured"}
          </div>
        </div>
        <div className="glass rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-mute)] mb-1">WA reminders sent (all-time)</div>
          <div className="text-sm font-semibold text-pink-300">
            {Object.values(counts).reduce((a, b) => a + (b || 0), 0)}
          </div>
        </div>
        <div className="glass rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-mute)] mb-1">Jobs registered</div>
          <div className="text-sm font-semibold text-fuchsia-300">{jobs.length} active</div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-[var(--text-dim)] flex items-center gap-2">
          <Activity className="w-4 h-4 text-emerald-300" /> Live — auto-refresh every 10 s
        </div>
        <button
          onClick={load}
          className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 inline-flex items-center gap-1.5"
          data-testid="bg-jobs-refresh-btn"
        >
          <RefreshCw className="w-3 h-3" /> Refresh now
        </button>
      </div>

      {/* Jobs table */}
      <div className="glass rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="text-left text-xs uppercase tracking-wider text-[var(--text-mute)] bg-white/5">
            <tr>
              <th className="px-4 py-3">Job</th>
              <th className="px-4 py-3">Interval</th>
              <th className="px-4 py-3">Last Run</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Result</th>
              <th className="px-4 py-3">Next Run</th>
              <th className="px-4 py-3">Runs / Errors</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-[var(--text-mute)]">No scheduled jobs.</td></tr>
            )}
            {jobs.map((j) => {
              const r = j.last_result || {};
              const summary = r.error
                ? <span className="text-red-300 text-xs" title={r.error}>{String(r.error).slice(0, 60)}</span>
                : (typeof r.checked === "number")
                  ? <span className="text-xs text-[var(--text-dim)]">checked <b>{r.checked}</b> · sent <b className="text-emerald-300">{r.sent || 0}</b>{r.skipped ? ` · skipped ${r.skipped}` : ""}</span>
                  : <span className="text-xs text-[var(--text-mute)]">—</span>;
              return (
                <tr key={j.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`bg-job-row-${j.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <JobIcon id={j.id} />
                      <div>
                        <div className="font-medium">{j.name || j.id}</div>
                        <code className="text-[10px] text-[var(--text-mute)]">{j.id}</code>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">{fmtInterval(j.interval_sec)}</td>
                  <td className="px-4 py-3 text-xs" title={j.last_run || ""}>
                    {fmtRelative(j.last_run)}
                    {j.last_duration_ms != null && (
                      <span className="ml-1 text-[var(--text-mute)]">({j.last_duration_ms} ms)</span>
                    )}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={j.last_status} /></td>
                  <td className="px-4 py-3">{summary}</td>
                  <td className="px-4 py-3 text-xs" title={j.next_run || ""}>
                    {j.paused ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 text-[10px] font-semibold">
                        <Pause className="w-2.5 h-2.5" /> PAUSED
                      </span>
                    ) : (
                      fmtRelative(j.next_run)
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <b className="text-fuchsia-300">{j.runs_total}</b>
                    {j.errors_total > 0 && <span className="text-red-300"> · {j.errors_total} errors</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => triggerJob(j.id)}
                        disabled={!!triggering[j.id] || j.paused}
                        className="text-xs px-2.5 py-1.5 rounded-full bg-pink-500/15 hover:bg-pink-500/25 text-pink-300 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`bg-job-run-${j.id}`}
                        title={j.paused ? "Resume first to run this job" : "Run this job now"}
                      >
                        <Play className="w-3 h-3" /> {triggering[j.id] ? "…" : "Run"}
                      </button>
                      <button
                        onClick={() => togglePause(j)}
                        className={`text-xs px-2.5 py-1.5 rounded-full inline-flex items-center gap-1 ${
                          j.paused
                            ? "bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300"
                            : "bg-amber-500/15 hover:bg-amber-500/25 text-amber-300"
                        }`}
                        data-testid={`bg-job-${j.paused ? "resume" : "pause"}-${j.id}`}
                        title={j.paused ? "Resume this job" : "Pause this job — persists across restarts"}
                      >
                        {j.paused ? <><Play className="w-3 h-3" /> Resume</> : <><Pause className="w-3 h-3" /> Pause</>}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Template counts */}
      <div className="glass rounded-2xl p-4">
        <div className="text-xs uppercase tracking-wider text-[var(--text-mute)] mb-3">
          WhatsApp template sends — all-time
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 text-center">
          {[
            ["class_starting_soon_15m", "15-min class"],
            ["demo_reminder_24h", "24-h demo"],
            ["abandoned_cart", "Abandoned cart"],
            ["balance_due_day25", "Balance · day-25"],
            ["balance_due_day28", "Balance · day-28"],
            ["certificate_ready", "Certificate"],
          ].map(([k, label]) => (
            <div key={k} className="rounded-xl bg-white/5 p-3" data-testid={`bg-count-${k}`}>
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-mute)]">{label}</div>
              <div className="text-xl font-bold text-pink-300 mt-1">{counts[k] || 0}</div>
            </div>
          ))}
        </div>
      </div>

      <SendTestSection />
      <ABVariantsSection />
      <AuditLogSection />
    </div>
  );
}

// ============================================================== SEND TEST
const TEMPLATE_KEYS = [
  ["lead_welcome",        "Lead welcome (name, course)",         ["Rahul", "Digital Marketing"]],
  ["syllabus_delivery",   "Syllabus delivery (name, course)",    ["Rahul", "Digital Marketing"]],
  ["demo_reminder",       "Demo reminder (name, time, course)",  ["Rahul", "7:00 PM", "Digital Marketing"]],
  ["class_starting_soon", "Class starting soon (name, course, topic, join_url)", ["Rahul", "Meta Ads", "Live setup", "https://zoom.us/j/123"]],
  ["payment_received",    "Payment received (name, amount, course, order_id)", ["Rahul", "9999", "Digital Marketing", "order_test_123"]],
  ["balance_due",         "Balance due (name, amount, course, days)", ["Rahul", "12000", "Digital Marketing", "5"]],
  ["abandoned_cart",      "Abandoned cart (name, course, seats, slug)", ["Rahul", "Digital Marketing", "3", "digital-marketing"]],
  ["certificate_ready",   "Certificate ready (name, course)",    ["Rahul", "SEO Specialist Course"]],
  ["webinar_reminder",    "Webinar reminder (join_url)",         ["https://zoom.us/j/123"]],
];

function SendTestSection() {
  const [key, setKey] = useState("lead_welcome");
  const [phone, setPhone] = useState("9131341638");
  const [argsText, setArgsText] = useState("Rahul, Digital Marketing");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const tpl = TEMPLATE_KEYS.find((t) => t[0] === key);
    if (tpl) setArgsText(tpl[2].join(", "));
  }, [key]);

  const send = async () => {
    const args = argsText.split(",").map((s) => s.trim()).filter(Boolean);
    setSending(true); setResult(null);
    try {
      const { data } = await api.post(`/admin/wa-test/${key}`, { phone, args });
      setResult(data);
      if (data.success) toast.success("Sent ✓");
      else toast.error(data.message || "Failed");
    } catch (e) { toast.error("Request failed"); }
    finally { setSending(false); }
  };

  return (
    <div className="glass rounded-2xl p-4 space-y-3" data-testid="send-test-section">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--text-mute)]">
        <Send className="w-3.5 h-3.5 text-pink-300" /> Send test WhatsApp template
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="block">
          <div className="text-[10px] text-[var(--text-mute)] mb-1">Template</div>
          <select value={key} onChange={(e) => setKey(e.target.value)}
                  className="bg-black/40 border border-white/10 rounded-md px-2 py-2 text-xs w-full"
                  data-testid="send-test-key">
            {TEMPLATE_KEYS.map(([k, label]) => <option key={k} value={k}>{k} — {label.split("(")[0].trim()}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="text-[10px] text-[var(--text-mute)] mb-1">Phone (10-digit India)</div>
          <input value={phone} onChange={(e) => setPhone(e.target.value)}
                 className="bg-black/40 border border-white/10 rounded-md px-2 py-2 text-xs w-full"
                 data-testid="send-test-phone" />
        </label>
        <label className="block">
          <div className="text-[10px] text-[var(--text-mute)] mb-1">Args (comma-separated)</div>
          <input value={argsText} onChange={(e) => setArgsText(e.target.value)}
                 className="bg-black/40 border border-white/10 rounded-md px-2 py-2 text-xs w-full"
                 data-testid="send-test-args" />
        </label>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={send} disabled={sending}
                className="text-xs px-4 py-2 rounded-full bg-pink-500/20 hover:bg-pink-500/30 text-pink-300 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="send-test-btn">
          <Send className="w-3 h-3" /> {sending ? "Sending…" : "Send now"}
        </button>
        {result && (
          <div className={`text-xs ${result.success ? "text-emerald-300" : "text-red-300"}`} data-testid="send-test-result">
            {result.success ? "✓ Accepted by Meta" : "✗"} · {result.message}
            {result.response?.messageId && <span className="ml-2 text-[var(--text-mute)]">id: {result.response.messageId.slice(0, 30)}…</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================== A/B VARIANTS
function ABVariantsSection() {
  const [data, setData] = useState({ variants_by_key: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [newKey, setNewKey] = useState("lead_welcome");
  const [newLabel, setNewLabel] = useState("");
  const [newSnippet, setNewSnippet] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get("/admin/wa-variants");
      setData(res);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newLabel.trim()) { toast.error("Label required"); return; }
    setCreating(true);
    try {
      await api.post("/admin/wa-variants", {
        template_key: newKey, label: newLabel, body_snippet: newSnippet, weight: 1, is_active: true,
      });
      toast.success("Variant created");
      setNewLabel(""); setNewSnippet("");
      load();
    } catch { toast.error("Create failed"); }
    finally { setCreating(false); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this variant?")) return;
    try { await api.delete(`/admin/wa-variants/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };
  const toggleActive = async (v) => {
    try {
      await api.patch(`/admin/wa-variants/${v.id}`, { is_active: !v.is_active });
      load();
    } catch { toast.error("Toggle failed"); }
  };

  return (
    <div className="glass rounded-2xl p-4 space-y-3" data-testid="ab-variants-section">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--text-mute)]">
        <FlaskConical className="w-3.5 h-3.5 text-fuchsia-300" /> A/B test variants
        <span className="ml-auto text-[10px] normal-case text-[var(--text-mute)]">
          {data.total} active · Razorpay conversion attributed within 14 days of send
        </span>
      </div>

      {/* Create row */}
      <div className="rounded-xl bg-white/[0.02] border border-white/5 p-3 grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
        <label className="block">
          <div className="text-[10px] text-[var(--text-mute)] mb-1">Template key</div>
          <select value={newKey} onChange={(e) => setNewKey(e.target.value)}
                  className="bg-black/40 border border-white/10 rounded-md px-2 py-2 text-xs w-full"
                  data-testid="ab-new-key">
            {TEMPLATE_KEYS.map(([k]) => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="text-[10px] text-[var(--text-mute)] mb-1">Label</div>
          <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Variant A — friendly tone"
                 className="bg-black/40 border border-white/10 rounded-md px-2 py-2 text-xs w-full" data-testid="ab-new-label" />
        </label>
        <label className="block">
          <div className="text-[10px] text-[var(--text-mute)] mb-1">Snippet (your reference, not Meta-controlled)</div>
          <input value={newSnippet} onChange={(e) => setNewSnippet(e.target.value)} placeholder="Hey there! Apke liye..."
                 className="bg-black/40 border border-white/10 rounded-md px-2 py-2 text-xs w-full" data-testid="ab-new-snippet" />
        </label>
        <button onClick={create} disabled={creating}
                className="text-xs px-3 py-2 rounded-full bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-300 inline-flex items-center gap-1 justify-center disabled:opacity-50"
                data-testid="ab-new-btn">
          <Plus className="w-3 h-3" /> {creating ? "Creating…" : "Add variant"}
        </button>
      </div>

      {/* Existing variants grouped by template */}
      {loading ? (
        <div className="text-xs text-[var(--text-mute)]">Loading variants…</div>
      ) : Object.keys(data.variants_by_key).length === 0 ? (
        <div className="text-xs text-[var(--text-mute)] py-4 text-center">No variants yet. Add 2-3 per template — the scheduler will weighted-random pick on every send and we'll attribute Razorpay conversions automatically.</div>
      ) : (
        <div className="space-y-3">
          {Object.entries(data.variants_by_key).map(([tplKey, variants]) => {
            const totalSent = variants.reduce((a, v) => a + (v.sent_count || 0), 0);
            const totalConv = variants.reduce((a, v) => a + (v.conversion_count || 0), 0);
            return (
              <div key={tplKey} className="rounded-xl bg-white/[0.02] border border-white/5 p-3">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <code className="text-xs text-fuchsia-300">{tplKey}</code>
                  <div className="text-[10px] text-[var(--text-mute)]">
                    Total: {totalSent} sent · {totalConv} converted · overall <b className="text-emerald-300">{totalSent ? (totalConv / totalSent * 100).toFixed(1) : 0}%</b>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {variants.map((v) => (
                    <div key={v.id} className="flex items-center gap-2 text-xs flex-wrap" data-testid={`ab-variant-${v.id}`}>
                      <button onClick={() => toggleActive(v)}
                              className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${v.is_active ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-[var(--text-mute)]"}`}>
                        {v.is_active ? "active" : "paused"}
                      </button>
                      <div className="font-medium">{v.label}</div>
                      {v.body_snippet && <span className="text-[var(--text-mute)] text-[11px] italic">"{v.body_snippet.slice(0, 60)}"</span>}
                      <span className="ml-auto text-[var(--text-mute)]">
                        weight {v.weight} · {v.sent_count} sent · {v.conversion_count} converted ·
                        <b className={`ml-1 ${v.conversion_rate_pct > 0 ? "text-emerald-300" : "text-[var(--text-mute)]"}`}>{v.conversion_rate_pct}%</b>
                      </span>
                      <button onClick={() => remove(v.id)} className="p-1 rounded hover:bg-red-500/10 text-red-300">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <VariantTimeseriesChart templateKey={tplKey} variants={variants} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================== AUDIT LOG
function AuditLogSection() {
  const [runs, setRuns] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter ? `/admin/scheduler/runs?job_id=${filter}&limit=50` : "/admin/scheduler/runs?limit=50";
      const { data } = await api.get(url);
      setRuns(data.runs || []);
    } catch { /* */ }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="glass rounded-2xl p-4 space-y-3" data-testid="audit-log-section">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--text-mute)]">
        <History className="w-3.5 h-3.5 text-cyan-300" /> Scheduler audit log
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="filter by job_id…"
               className="ml-auto bg-black/40 border border-white/10 rounded-md px-2 py-1 text-xs w-48"
               data-testid="audit-filter" />
        <button onClick={load} className="text-xs px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 inline-flex items-center gap-1">
          <RefreshCw className="w-3 h-3" />
        </button>
      </div>
      {loading ? (
        <div className="text-xs text-[var(--text-mute)]">Loading…</div>
      ) : runs.length === 0 ? (
        <div className="text-xs text-[var(--text-mute)] py-4 text-center">No runs recorded yet. Once a job fires its first run, this log will populate.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[600px]">
            <thead className="text-[10px] uppercase tracking-wider text-[var(--text-mute)]">
              <tr><th className="text-left p-1.5">When</th><th className="text-left p-1.5">Job</th><th className="text-left p-1.5">Status</th><th className="text-left p-1.5">Duration</th><th className="text-left p-1.5">Result</th></tr>
            </thead>
            <tbody>
              {runs.slice(0, 50).map((r, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="p-1.5 text-[var(--text-dim)]">{r.started_at?.slice(0, 19).replace("T", " ")}</td>
                  <td className="p-1.5"><code className="text-fuchsia-300">{r.job_id}</code></td>
                  <td className="p-1.5">
                    {r.status === "ok"
                      ? <span className="text-emerald-300">✓ ok</span>
                      : <span className="text-red-300">✗ error</span>}
                  </td>
                  <td className="p-1.5 text-[var(--text-mute)]">{r.duration_ms}ms</td>
                  <td className="p-1.5 text-[var(--text-dim)]">
                    {r.error
                      ? <span className="text-red-300">{r.error.slice(0, 80)}</span>
                      : (r.result && typeof r.result === "object"
                          ? Object.entries(r.result).map(([k, v]) => `${k}:${v}`).join(" · ")
                          : "—")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================== A/B TIMESERIES CHART
const VARIANT_COLORS = ["#ec4899", "#22d3ee", "#a855f7", "#f59e0b", "#10b981"];

function VariantTimeseriesChart({ templateKey, variants }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get(`/admin/wa-variants/timeseries?template_key=${templateKey}&days=14`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ days: [], series: [] }));
  }, [open, templateKey]);

  // Reshape to flat array of { date, [variantId]: sent, [variantId+'_conv']: converted, ...rates }
  const chartData = data?.days?.map((day, i) => {
    const row = { date: day.slice(5) };
    for (const s of data.series) {
      const p = s.points[i];
      const rate = p.sent ? (p.converted / p.sent) * 100 : 0;
      row[`${s.variant_id}_rate`] = Math.round(rate * 10) / 10;
    }
    return row;
  }) || [];

  const hasAnyData = chartData.some((d) =>
    Object.entries(d).some(([k, v]) => k !== "date" && v > 0)
  );

  return (
    <div className="mt-3 pt-3 border-t border-white/5" data-testid={`ab-chart-${templateKey}`}>
      <button onClick={() => setOpen((o) => !o)}
              className="text-[10px] text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1">
        <BarChart3 className="w-3 h-3" /> {open ? "Hide" : "Show"} 14-day conversion-rate chart
      </button>
      {open && (
        <div className="mt-2 h-44">
          {!data ? (
            <div className="text-xs text-[var(--text-mute)] text-center pt-8">Loading…</div>
          ) : !hasAnyData ? (
            <div className="text-xs text-[var(--text-mute)] text-center pt-8">
              No conversion data yet. Variants need ≥1 send + a Razorpay payment within 14 days to populate.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 10, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#888", fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#888", fontSize: 10 }} axisLine={false} tickLine={false}
                       tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  contentStyle={{ background: "#0a0d18", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }}
                  formatter={(v, name) => [`${v}%`, name]}
                />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {variants.map((v, i) => (
                  <Line key={v.id}
                        type="monotone"
                        dataKey={`${v.id}_rate`}
                        name={v.label}
                        stroke={VARIANT_COLORS[i % VARIANT_COLORS.length]}
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        activeDot={{ r: 5 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      )}
    </div>
  );
}

